import { handleApiError } from '@/shared/lib/errorHandler'
import { FormDialog } from '@/shared/components/dialogs/FormDialog'
import { ProjectEditForm, ProjectEditFormHandle } from '@/shared/components/projects/ProjectEditForm'
import { ProjectTimeline, TimelinePhase } from '@/shared/components/ProjectTimeline'
import { getProjectLevelStatusLabels, PROJECT_LEVEL_STATUS_COLORS } from '@/shared/constants/project-profile'
import { useGetProjectOverview } from '@/shared/hooks/projects/useGetProjectOverview'
import { useUpdateProject } from '@/shared/hooks/projects/useUpdateProject'
import { LandUseStructureView } from '@/shared/components/projects/LandUseStructureView'
import { useCustomFields } from '@/shared/hooks/projects/useCustomFields'
import { useCustomFieldValues } from '@/shared/hooks/projects/useCustomFieldValues'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { mapFormDataToApiUpdate, ProjectEditFormData } from '@/shared/api/projects-edit-form'
import { useUser } from '@/shared/store/auth.store'
import { api } from '@/shared/lib/api'
import { useCan } from '@/shared/lib/authorization'
import type { ProjectProgress } from '@workspace/lib/api'
import { createFileRoute, useLocation, Navigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@workspace/ui/components/Badge'
import { Button } from '@workspace/ui/components/Button'
import { toast } from '@workspace/ui/components/Sonner'
import { cn } from '@workspace/ui/lib/utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { PencilLine, Star, ChevronDown, ChevronUp } from 'lucide-react'
import QRCode from 'qrcode'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ADMIN_ROLE_NAME } from '@/shared/constants'
import { useProjectPermissions } from '@/shared/hooks/projects/useProjectPermissions'
import { auditLogHelpers } from '@/shared/lib/audit-log'
import { ca } from 'zod/v4/locales'

dayjs.extend(utc)

export const Route = createFileRoute('/projects/$id/_pathlessLayout/')({
    component: RouteComponent,
    beforeLoad: requirePermissionBeforeLoad('view', 'project'),
})

const mapProgressStatusToTimelineStatus = (status: ProjectProgress['status']): TimelinePhase['status'] => {
    switch (status) {
        case 'COMPLETED':
            return 'completed'
        case 'IN_PROGRESS':
            return 'in-progress'
        case 'NOT_STARTED':
        default:
            return 'not-started'
    }
}

const mapProgressesToPhases = (progresses: ProjectProgress[]): TimelinePhase[] => {
    return progresses
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map(progress => {
            return {
                id: progress.id,
                title: progress.name,
                startDate: progress.startDate,
                endDate: progress.endDate,
                status: mapProgressStatusToTimelineStatus(progress.status),
                progress: progress.progress,
            }
        })
}

const numberFormatter = new Intl.NumberFormat('en-US')

const DetailItem = ({ label, value }: { label: string; value?: number | string | null }) => {
    const isValueValid = value !== null && value !== undefined && value !== '' && String(value).trim() !== ''
    return (
        <div className="flex-1 flex flex-col gap-1">
            <span className="text-xs text-neutral-500 leading-4 font-normal">{label}</span>
            <span className="text-sm font-medium text-foreground">
                {isValueValid && !isNaN(Number(value)) ? numberFormatter.format(Number(value)) : '-'}
            </span>
        </div>
    )
}

function RouteComponent() {
    const { t } = useTranslation()
    const { id } = Route.useParams() as { id: string }
    const [isEditOpen, setEditOpen] = useState(false)
    const [isScaleExpanded, setIsScaleExpanded] = useState(false)
    const formRef = useRef<ProjectEditFormHandle>(null)
    const [statusBadge, setStatusBadge] = useState<ReactNode>(null)
    const { data: overview, isLoading } = useGetProjectOverview(id)
    const data = overview?.project
    const updateProjectMutation = useUpdateProject()

    const currentUser = useUser()
    const queryClient = useQueryClient()
    const { availableFields } = useCustomFields(id)
    const { customFieldValuesData } = useCustomFieldValues(data)

    const isAdmin = currentUser?.role?.name === ADMIN_ROLE_NAME
    const globalOperate = useCan('operate', 'project')
    const globalView = useCan('view', 'project')
    const projectPerm = useProjectPermissions(id)

    const canEditProject = isAdmin || projectPerm.canManage
    const canViewProject = isAdmin || globalView || projectPerm.canView

    React.useEffect(() => {
        if (data && canViewProject) {
            const projectName = data?.name ?? id
            auditLogHelpers
                .trackView('project', id, { description: t('audit.view.project', { name: projectName }) }, true)
                .catch(() => {})
        }
    }, [data, id, canViewProject])

    const { data: foldersData } = useQuery({
        queryKey: ['lazy-load', { projectId: id, page: 1, take: 1 }],
        queryFn: () =>
            api.folder.loadMultiFile({
                projectId: id,
                page: 1,
                take: 1,
                noPagination: false,
            }),
        enabled: !!id,
        meta: {
            skipGlobalErrorHandler: true,
        },
    })

    const hasFolders = useMemo(() => {
        // Only show timeline if we have folders data and it contains items
        if (!foldersData) {
            return false
        }

        const hasItems = Array.isArray(foldersData.items) && foldersData.items.length > 0
        const hasData = Array.isArray(foldersData.data) && foldersData.data.length > 0

        const isVisible = hasItems || hasData

        return isVisible
    }, [foldersData])

    const phases = useMemo(() => mapProgressesToPhases(data?.progresses ?? []), [data?.progresses])

    const optionalFields = useMemo(() => {
        const values = customFieldValuesData?.data ?? []

        if (values.length === 0) {
            return []
        }

        const availableFieldsMap = new Map<string, string>(availableFields.map(field => [field.id, field.label]))

        return values
            .filter(value => !value.deletedAt && value.fieldValue && value.fieldValue.trim() !== '')
            .map(value => {
                const labelFromApi = availableFieldsMap.get(value.fieldId)
                const fallbackLabel = value.customField?.name || ''
                return {
                    id: value.fieldId,
                    label: labelFromApi || fallbackLabel,
                    value: value.fieldValue,
                }
            })
            .filter(field => field.label && field.value)
    }, [availableFields, customFieldValuesData])

    const handleEditSubmit = async (formData: ProjectEditFormData) => {
        if (!globalView) {
            toast.error({ title: t('common.status.error'), description: t('projects.detail.info.noPermission') })
            return
        }
        try {
            // Update project basic info
            const updateData = mapFormDataToApiUpdate(formData, currentUser?.id)
            await updateProjectMutation.mutateAsync({ id, data: updateData })
            // Handle custom field values
            try {
                // Get existing custom field values for this project
                let existingValues: any[] = []
                try {
                    const existingValuesResponse = await api.customFieldValue.listByProject(id, {
                        page: 1,
                        take: 100,
                    })
                    existingValues = (existingValuesResponse.data || []).filter((v: any) => !v.deletedAt)
                } catch {
                    // If fetch fails, treat as empty array
                    existingValues = []
                }

                const existingMap = new Map(existingValues.map((v: any) => [v.fieldId, v]))
                const formFieldIds = new Set((formData.optionalFields || []).map(f => f.id))
                const deletions: Promise<any>[] = []
                const fieldsToUpsert: Array<{ fieldId: string; fieldValue: string }> = []

                // Process form fields: collect all fields that have values (for create or update)
                if (formData.optionalFields && formData.optionalFields.length > 0) {
                    formData.optionalFields.forEach(field => {
                        const existing = existingMap.get(field.id)
                        const trimmedValue = field.value?.trim() || ''

                        if (trimmedValue) {
                            // Field has value - add to upsert list (create or update)
                            fieldsToUpsert.push({
                                fieldId: field.id,
                                fieldValue: trimmedValue,
                            })
                        } else if (existing) {
                            // Field is empty but has existing value - delete it
                            deletions.push(
                                (async () => {
                                    try {
                                        return await api.customFieldValue.delete(existing.id)
                                    } catch (err) {
                                        console.error('Error deleting customFieldValue', {
                                            id: existing.id,
                                            error: err,
                                            existing,
                                        })
                                        throw err
                                    }
                                })(),
                            )
                        }
                    })
                }

                // Delete values for fields that were removed from the form
                existingValues.forEach(existingValue => {
                    if (!formFieldIds.has(existingValue.fieldId)) {
                        deletions.push(api.customFieldValue.delete(existingValue.id))
                    }
                })

                // Bulk upsert all fields that have values (create new or update existing)
                if (fieldsToUpsert.length > 0) {
                    try {
                        await api.customFieldValue.bulkUpsert({
                            entityType: 'project',
                            entityId: id,
                            customFields: fieldsToUpsert,
                            projectId: id,
                            createdById: currentUser?.id,
                        })
                    } catch (upsertError: any) {
                        // Log detailed error for debugging
                        console.error('Error upserting custom field values:', upsertError)
                        if (upsertError?.response) {
                            console.error('Upsert Error Response:', {
                                status: upsertError.response.status,
                                statusText: upsertError.response.statusText,
                                data: upsertError.response.data,
                                url: upsertError.config?.url,
                                payload: upsertError.config?.data,
                            })
                        }
                        // Re-throw to be caught by outer catch
                        throw upsertError
                    }
                }

                // Execute all deletions
                if (deletions.length > 0) {
                    await Promise.all(deletions)
                }

                // Invalidate custom field values query to refetch updated data
                queryClient.invalidateQueries({ queryKey: ['customFieldValues', id] })
            } catch (customFieldError) {
                // Show warning but don't fail the entire operation
                toast.warning({
                    title: t('common.status.warning'),
                    description: t(
                        'projects.detail.info.customFieldWarning',
                        'Dự án đã được cập nhật nhưng có lỗi khi lưu một số trường tùy chọn',
                    ),
                })
            }

            toast.success({
                title: t('common.status.success'),
                description: t('projects.detail.info.updateSuccess', 'Cập nhật dự án thành công !'),
            })
            setEditOpen(false)
        } catch (error: any) {
            // Error is handled automatically by TanStack Query MutationCache (or custom error handler)
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">{t('common.loading')}</p>
            </div>
        )
    }

    if (!data) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">{t('projects.detail.notFound')}</p>
            </div>
        )
    }

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="border border-border rounded-xl p-4 max-lg:p-3 flex flex-col gap-4 max-lg:gap-3">
                    <div className="flex justify-between items-center">
                        <h2 className="text-base font-semibold text-text-heading">{t('projects.detail.info.title')}</h2>
                        {canEditProject && (
                            <Button
                                variant="unstyled"
                                className="text-primary hover:text-primary/80 hover:bg-primary/10 text-sm font-bold"
                                size="sm"
                                onPress={() => setEditOpen(true)}
                            >
                                <PencilLine className="size-4!" />
                                {t('projects.detail.info.editButton')}
                            </Button>
                        )}
                    </div>

                    {/* QR Code + Project info */}
                    <div className="flex gap-4 max-lg:flex-col max-lg:items-center max-lg:gap-3">
                        {/* QR Code */}
                        <QrRender />

                        {/* Project name, status, updated info */}
                        <div className="flex flex-col gap-1 flex-1 max-lg:items-center max-lg:text-center">
                            <div className="flex items-center gap-2 max-lg:justify-center flex-wrap">
                                <h3 className="text-sm font-bold text-text-heading">{data?.name}</h3>
                                {data?.isKeyProject && (
                                    <Badge className="bg-yellow-100 hover:bg-yellow-100 text-yellow-700 border-yellow-200 uppercase text-[10px] px-1.5 py-0 rounded-sm font-bold flex items-center gap-1 shadow-none">
                                        <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                                        Dự án trọng điểm
                                    </Badge>
                                )}
                            </div>
                            <Badge
                                className={cn(
                                    'w-fit rounded-full px-2 py-0.5 text-xs font-medium border',
                                    PROJECT_LEVEL_STATUS_COLORS[data?.status],
                                )}
                            >
                                {getProjectLevelStatusLabels(t)[data?.status]}
                            </Badge>
                            <p className="text-xs text-neutral-500">
                                {t('projects.detail.info.updatedBy')}{' '}
                                <span className="font-semibold text-foreground">{data?.updatedBy?.fullName}</span>{' '}
                                {t('projects.detail.info.at')} {/* Format UTC datetime to local timezone */}
                                {data?.updatedAt ? dayjs.utc(data.updatedAt).local().format('DD/MM/YYYY HH:mm') : '-'}
                            </p>
                        </div>
                    </div>

                    {/* Details grid */}
                    <div className="flex flex-col gap-4 max-lg:gap-3">
                        {/* Row 1 */}
                        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1 max-lg:gap-2">
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500">
                                    {t('projects.detail.info.fields.code')}
                                </span>
                                <span className="text-sm font-medium text-foreground break-all">
                                    {data?.code ?? '-'}
                                </span>
                            </div>
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500">
                                    {t('projects.detail.info.fields.investor')}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                    {data?.investor?.name ?? '-'}
                                </span>
                            </div>
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500">
                                    {t('projects.detail.info.fields.regionArea')}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                    {data?.region?.name ?? '-'} - {data?.area?.name ?? '-'}
                                </span>
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1 max-lg:gap-2">
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500 leading-4 font-normal">
                                    {t('projects.detail.info.fields.manager')}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                    {data?.manager?.fullName ?? '-'}
                                </span>
                            </div>
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500 leading-4 font-normal">
                                    {t('projects.detail.info.fields.projectType')}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                    {data?.projectType?.name ?? '-'}
                                </span>
                            </div>
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500 leading-4 font-normal">
                                    {t('projects.detail.info.fields.keywords')}
                                </span>
                                <span className="text-sm font-medium text-foreground">{data?.keyword ?? '-'}</span>
                            </div>
                        </div>

                        {/* Row 3 - Start & End dates - Format UTC to local timezone */}
                        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1 max-lg:gap-2">
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500 leading-4 font-normal">
                                    {t('projects.detail.info.fields.startDate')}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                    {data?.startDate ? dayjs.utc(data.startDate).local().format('DD/MM/YYYY') : '-'}
                                </span>
                            </div>
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500 leading-4 font-normal">
                                    {t('projects.detail.info.fields.expectedEndDate')}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                    {data?.endDate ? dayjs.utc(data.endDate).local().format('DD/MM/YYYY') : '-'}
                                </span>
                            </div>
                            <div className="flex-1 flex flex-col gap-1">
                                <span className="text-xs text-neutral-500 leading-4 font-normal">
                                    {t('projects.form.labels.functionalDepartment')}
                                </span>
                                <span className="text-sm font-medium text-foreground">
                                    {data?.functionalDepartment?.name ?? '-'}
                                </span>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-neutral-500 leading-4 font-normal">
                                {t('projects.detail.info.fields.description')}
                            </span>
                            <p className="text-sm font-medium text-foreground whitespace-pre-line">
                                {data?.description ?? '-'}
                            </p>
                        </div>

                        {/* Quy mô & Đầu tư */}
                        <div className="flex flex-col gap-4 mt-2">
                            <div
                                className={cn(
                                    'flex justify-between items-center cursor-pointer',
                                    isScaleExpanded && 'border-b pb-2',
                                )}
                                onClick={() => setIsScaleExpanded(!isScaleExpanded)}
                            >
                                <h3 className="text-sm font-semibold text-text-heading">
                                    {t('projects.detail.info.scaleAndCostTitle')}
                                </h3>
                                {isScaleExpanded ? (
                                    <ChevronUp className="w-5 h-5 text-neutral-500" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-neutral-500" />
                                )}
                            </div>

                            {isScaleExpanded && (
                                <>
                                    {/* Hàng 4: Chi phí & Đầu tư */}
                                    <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1 max-lg:gap-2">
                                        <DetailItem
                                            label={t('projects.detail.info.fields.totalInvestment')}
                                            value={data?.totalInvestment}
                                        />
                                        <DetailItem
                                            label={t('projects.detail.info.fields.compensationCost')}
                                            value={data?.compensationCost}
                                        />
                                        <DetailItem
                                            label={t('projects.detail.info.fields.expectedLandUseFee')}
                                            value={data?.expectedLandUseFee}
                                        />
                                    </div>

                                    {/* Hàng 5: Quy mô diện tích đất (ha) */}
                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm font-medium text-text-heading">
                                            {t('projects.detail.info.scaleTitle')}
                                        </span>
                                        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1 max-lg:gap-2">
                                            <DetailItem
                                                label={t('projects.detail.info.fields.areaScaleResidential')}
                                                value={data?.areaScaleResidential}
                                            />
                                            <DetailItem
                                                label={t('projects.detail.info.fields.areaScaleCommercial')}
                                                value={data?.areaScaleCommercial}
                                            />
                                            <DetailItem
                                                label={t('projects.detail.info.fields.areaScaleOther')}
                                                value={data?.areaScaleOther}
                                            />
                                        </div>
                                    </div>

                                    {/* Hàng 6: Diện tích đất đã được giao (ha) */}
                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm font-medium text-text-heading">
                                            {t('projects.detail.info.allocatedTitle')}
                                        </span>
                                        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1 max-lg:gap-2">
                                            <DetailItem
                                                label={t('projects.detail.info.fields.allocatedAreaResidential')}
                                                value={data?.allocatedAreaResidential}
                                            />
                                            <DetailItem
                                                label={t('projects.detail.info.fields.allocatedAreaCommercial')}
                                                value={data?.allocatedAreaCommercial}
                                            />
                                            <DetailItem
                                                label={t('projects.detail.info.fields.allocatedAreaOther')}
                                                value={data?.allocatedAreaOther}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Cơ cấu sử dụng đất */}
                            <LandUseStructureView data={data?.landUseStructure} />
                        </div>

                        {/* Optional Fields */}
                        {optionalFields.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <span className="text-xs text-neutral-500 leading-4 font-normal">
                                    {t('projects.detail.info.optionalFieldsTitle', 'Trường tùy chọn')}
                                </span>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {optionalFields.map(field => (
                                        <div key={field.id} className="flex flex-col gap-1">
                                            <span className="text-xs text-neutral-500 leading-4 font-normal">
                                                {field.label}
                                            </span>
                                            <span className="text-sm font-medium text-foreground whitespace-pre-line">
                                                {field.value ?? '-'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {hasFolders && phases.length > 0 && (
                    <div className="border border-border rounded-xl p-4 flex flex-col gap-4">
                        <h2 className="text-base font-semibold text-text-heading">
                            {t('projects.detail.timelineTitle')}
                        </h2>
                        <ProjectTimeline phases={phases} />
                    </div>
                )}
            </div>
            {canEditProject && (
                <FormDialog
                    open={isEditOpen}
                    onOpenChange={setEditOpen}
                    title={
                        <div className="flex items-center gap-2">
                            <span className="whitespace-nowrap shrink-0">
                                {t('projects.detail.info.editDialogTitle', 'Chỉnh sửa thông tin dự án')}
                            </span>
                            {statusBadge}
                        </div>
                    }
                    contentClassName="max-w-[960px] w-full"
                    primaryActionLabel="Lưu"
                    cancelActionLabel="Hủy"
                    primaryActionVariant="default"
                    primaryActionClassName="bg-background-accent text-text-accent hover:bg-background-accent/90"
                    isLoading={updateProjectMutation.isPending}
                    onPrimaryAction={() => {
                        formRef.current?.submit()
                    }}
                    onCancel={() => setEditOpen(false)}
                >
                    <ProjectEditForm
                        ref={formRef}
                        onSubmit={handleEditSubmit}
                        onCancel={() => setEditOpen(false)}
                        isSubmitting={updateProjectMutation.isPending}
                        onStatusBadgeRender={setStatusBadge}
                        projectDetail={data}
                        canOperate={canEditProject}
                    />
                </FormDialog>
            )}
        </>
    )
}

const QrRender = () => {
    const location = useLocation()
    const { t } = useTranslation()
    const [qr, setQr] = useState<string | null>(null)

    useEffect(() => {
        const fetchQr = async () => {
            try {
                const qrDataUrl = await QRCode.toDataURL(window.location.origin + location.href, {
                    width: 200,
                    margin: 1,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF',
                    },
                })
                setQr(qrDataUrl)
            } catch (error) {
                console.error('Failed to generate QR code', error)
            }
        }
        fetchQr()
    }, [location.href])

    return (
        <>
            {qr ? (
                <img src={qr} alt={t('projects.detail.info.qrCode')} className="w-16 h-16 rounded shrink-0" />
            ) : (
                <div
                    className="w-16 h-16 bg-muted-foreground rounded shrink-0 animate-pulse"
                    aria-label={t('projects.detail.info.qrCode')}
                />
            )}
        </>
    )
}
